import React, { useCallback } from "react";
import { Helmet } from "react-helmet";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";
import { fetchEsimBatchAdmin } from "../../services/EsimService";
import brand from "../../config/brand";

const columns = [
  { key: "iccid", label: "ICCID", width: "200px" },
  { key: "last_package_name", label: "Last Package Name", width: "400px" },

  { key: "user_id", label: "User ID", width: "300px" },
  { key: "country_name", label: "Country", width: "200px" },
  { key: "region_name", label: "Region", width: "200px" },

  { key: "status", label: "Status", width: "140px" },
  { key: "installed", label: "Installed", width: "140px" },
  { key: "data_usage_percent", label: "Usage %", width: "300px" },
  { key: "updated_at", label: "Updated At", width: "300px" },
];

const optionalColumns = [
  { key: "imsi", label: "IMSI", width: "180px" },
  { key: "activation_code", label: "Activation Code", width: "400px" },
  { key: "data_usage", label: "Data Usage(MB)", width: "300px" },
  { key: "data_left", label: "Data Left(MB)", width: "300px" },
  { key: "data_limit", label: "Data Limit(MB)", width: "300px" },
  { key: "created_at", label: "Created At", width: "300px" },
  { key: "date_end", label: "Date End", width: "300px" },
  { key: "activate_before", label: "Activate Before", width: "300px" },
];

const sortOptions = [
  { value: "iccid", label: "ICCID" },
  { value: "imsi", label: "IMSI" },
  { value: "status", label: "Status" },
  { value: "installed", label: "Installed" },
  { value: "user_id", label: "User ID" },
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
  { value: "data_usage", label: "Data Usage(MB)" },
  { value: "data_left", label: "Data Left(MB)" },
  { value: "data_limit", label: "Data Limit(MB)" },
  { value: "date_end", label: "Data End" },
  { value: "activate_before", label: "Activate Before" },
  { value: "data_usage_percent", label: "Usage %" },
];

const statusOptions = ["inactive", "active", "terminated", "archived"];

const filterDefinitions = [
  { key: "iccid", type: "text" },
  { key: "imsi", type: "text" },
  { key: "user_id", type: "text" },
  // { key: "last_package_name", type: "text" },
  {
    key: "status",
    type: "select",
    options: statusOptions.map((opt) => ({
      value: opt,
      label: opt.charAt(0).toUpperCase() + opt.slice(1),
    })),
  },
  {
    key: "installed",
    type: "select",
    options: [
      { value: "true", label: "Installed" },
      { value: "false", label: "Not Installed" },
    ],
  },
  {
    key: "data_usage",
    type: "minmax",
    inputType: "number",
  },
  {
    key: "data_limit",
    type: "minmax",
    inputType: "number",
  },
  {
    key: "data_left",
    type: "minmax",
    inputType: "number",
  },
  {
    key: "date_end",
    type: "date-range",
  },
  {
    key: "data_usage_percent",
    type: "minmax",
    inputType: "number",
  },
  {
    key: "created_at",
    type: "date-range",
  },
  {
    key: "updated_at",
    type: "date-range",
  },
  {
    key: "activate_before",
    type: "date-range",
  },
  { key: "country_name", type: "text" },
  { key: "region_name", type: "text" },
];

function formatDate(dateString) {
  if (!dateString) return "-";
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function formatNumberNoDecimal(num) {
  if (num === null || num === undefined) return "-";
  return Math.round(num).toString();
}

export default function EsimStats() {
  const fetchDataFunction = useCallback(async ({ token, skip, limit, sort_by, sort_order, filters }) => {
    const apiFilters = {};

    if (filters.iccid) apiFilters.iccid = filters.iccid;
    if (filters.imsi) apiFilters.imsi = filters.imsi;
    if (filters.status) apiFilters.status = filters.status;
    if (filters.user_id) apiFilters.user_id = filters.user_id;

    if (filters.installed !== "") {
      apiFilters.installed = filters.installed === "true";
    }

    if (filters.min_data_usage) apiFilters.min_data_usage = Number(filters.min_data_usage);
    if (filters.max_data_usage) apiFilters.max_data_usage = Number(filters.max_data_usage);

    if (filters.min_data_left) {
      apiFilters.min_data_left = Number(filters.min_data_left);
    }
    if (filters.max_data_left) {
      apiFilters.max_data_left = Number(filters.max_data_left);
    }

    if (filters.min_data_limit) apiFilters.min_data_limit = Number(filters.min_data_limit);
    if (filters.max_data_limit) apiFilters.max_data_limit = Number(filters.max_data_limit);

    if (filters.min_data_usage_percent) {
      apiFilters.min_data_usage_percent = Number(filters.min_data_usage_percent);
    }
    if (filters.max_data_usage_percent) {
      apiFilters.max_data_usage_percent = Number(filters.max_data_usage_percent);
    }

    if (filters.min_created_at) apiFilters.min_created_at = filters.min_created_at;
    if (filters.max_created_at) apiFilters.max_created_at = filters.max_created_at;
    if (filters.min_updated_at) apiFilters.min_updated_at = filters.min_updated_at;
    if (filters.max_updated_at) apiFilters.max_updated_at = filters.max_updated_at;

    if (filters.country_name) apiFilters.country_name = filters.country_name;
    if (filters.region_name) apiFilters.region_name = filters.region_name;

    const data = await fetchEsimBatchAdmin({
      token,
      skip,
      limit,
      sort_by,
      sort_order,
      filters: apiFilters,
    });

    return {
      items: data.items || [],
      total: data.total || 0,
      filtered: data.filtered ?? 0,
    };
  }, []);

  const customRenderers = {
    installed: (item) =>
      item.installed ? (
        <span className="badge-active">Installed</span>
      ) : (
        <span className="badge-inactive">Not Installed</span>
      ),

    created_at: (item) => formatDate(item.created_at),
    updated_at: (item) => formatDate(item.updated_at),
    date_end: (item) => formatDate(item.date_end),
    activate_before: (item) => formatDate(item.activate_before),

    data_usage: (item) => formatNumberNoDecimal(item.data_usage),
    data_left: (item) => formatNumberNoDecimal(item.data_left),
    data_limit: (item) => formatNumberNoDecimal(item.data_limit),

    country_name: (item) => item.country_name || "-",
    region_name: (item) => item.region_name || "-",

    data_usage_percent: (item) =>
      item.data_usage_percent != null ? `${Number(item.data_usage_percent).toFixed(2)}%` : "-",
  };

  return (
    <main className="esim-stats-page">
      <Helmet>
        <title>{brand.name} - eSIM Stats</title>
      </Helmet>

      <PageHeader title="EsimStats" description="View Esims bought by users" />

      <section className="esim-states partner-offers__wrap">
        <DataTable
          tableId="esim-stats"
          fetchDataFunction={fetchDataFunction}
          columns={columns}
          optionalColumns={optionalColumns}
          sortOptions={sortOptions}
          filterDefinitions={filterDefinitions}
          defaultSortBy="updated_at"
          itemKey="iccid"
          customRenderers={customRenderers}
          showSort
          showFilters
          showPageSize
          defaultPageSize={20}
          emptyMessage="No results"
        />
      </section>
    </main>
  );
}
